import React, { useState, useEffect } from 'react';
import { V } from '../../utils/theme';
import { LS } from '../../utils/storage';
import { Card, Btn } from '../../components/ui';
import { apiPost, ApiError } from '../../utils/api';
import { useLayout } from '../../utils/responsive';
import { NOTIF_TYPES } from '../../data/notifTypes';
import { filterAudience } from './orgs/helpers';

// Caps for resolving a push audience from the org API. Production has one org with two
// members; the caps stop a large tenant estate turning one screen into hundreds of calls.
const PUSH_ORG_MAX=10, SA_DETAIL_MEMBER_CAP=500, DEFAULT_BRAND="IRONLOG";

export function AdminPush({s}){
  const { isDesktop } = useLayout();
  const email=s.profile?.email;

  // ── Shared user list (loaded once, used by both push + in-app sections) ──
  const [users,setUsers]=useState(null);
  const [usersLoading,setUsersLoading]=useState(false);
  const [usersError,setUsersError]=useState(null);
  const [userSearch,setUserSearch]=useState("");
  const [dropdownOpen,setDropdownOpen]=useState(false);

  // ── Push section state ──
  const [pushTargetMode,setPushTargetMode]=useState("all"); // "all" | "selected"
  const [pushSelected,setPushSelected]=useState(new Set()); // emails
  const [title,setTitle]=useState(DEFAULT_BRAND);
  const [body,setBody]=useState("");
  const [sending,setSending]=useState(false);
  const [result,setResult]=useState(null);
  const [subCount,setSubCount]=useState(null);
  const [history,setHistory]=useState(()=>LS.get("ft-admin-push-history")||[]);

  // ── In-app section state ──
  const [inAppTargetMode,setInAppTargetMode]=useState("all");
  const [inAppSelected,setInAppSelected]=useState(new Set());
  const [inAppDropdownOpen,setInAppDropdownOpen]=useState(false);
  const [inAppSearch,setInAppSearch]=useState("");
  const [notifFlags,setNotifFlags]=useState({streak_reminder:false,double_xp:false,comeback:false,mission_reminder:false,shield_awarded:false,custom:false});
  const [customMsg,setCustomMsg]=useState("");
  const [customTitle,setCustomTitle]=useState("");
  const [notifSending,setNotifSending]=useState(false);
  const [notifResult,setNotifResult]=useState(null);

  // ── Audience (white-label) ──
  // /api/push has no org dimension: `send` targets a single address or every active
  // subscription, nothing in between. So the picker resolves the recipients here — one
  // org's active roster, or everyone who is in no org at all — and the send loop goes one
  // address at a time, exactly like the existing "specific users" path.
  //
  // The roster comes from /api/org `sa_org_detail`, which answers with the members AND the
  // tenant's branding in a single call; that is what makes the default notification title
  // the gym's own app name instead of "IRONLOG", which its members have never seen.
  // Reading a roster is super-admin only, so a plain admin gets a named reason, not a
  // silent empty audience.
  const [audience,setAudience]=useState("all"); // all | org | solo
  const [orgs,setOrgs]=useState(null);
  const [orgsError,setOrgsError]=useState(null);
  const [orgId,setOrgId]=useState("");
  const [roster,setRoster]=useState(null); // { emails, truncated } for the chosen audience
  const [rosterError,setRosterError]=useState(null);
  const [rosterBusy,setRosterBusy]=useState(false);
  const [brandName,setBrandName]=useState(DEFAULT_BRAND);
  const [titleTouched,setTitleTouched]=useState(false);

  const loadUsers=()=>{
    if(users!==null||usersLoading)return;
    setUsersLoading(true);setUsersError(null);
    apiPost("/api/admin",{action:"users"},{email})
      .then(j=>{setUsers(j?.users||[]);setUsersLoading(false);})
      .catch(e=>{
        const msg=e instanceof ApiError
          ?(e.status===401||e.status===403?"Session expired — sign in again":e.body?.error||`Failed to load users (HTTP ${e.status})`)
          :"Failed to load users — check connection";
        setUsersError(msg);setUsersLoading(false);
      });
  };

  useEffect(()=>{
    if(!email)return;
    // Abortable fetch + ignored-flag guard against late responses arriving after
    // email change or unmount (avoids out-of-order setState and stale data).
    const ctrl=new AbortController();
    let ignore=false;
    apiPost("/api/push",{action:"count"},{email,signal:ctrl.signal})
      .then(j=>{if(!ignore && j.count!=null)setSubCount(j.count);})
      .catch(()=>{});
    return()=>{ignore=true;ctrl.abort();};
  },[email]);

  // org_list is admin-level (no invite codes, no emails) — it is only the picker.
  const loadOrgs=async()=>{
    if(orgs!==null)return orgs;
    try{
      const j=await apiPost("/api/admin",{action:"org_list"},{email});
      const rows=j?.orgs||[];setOrgs(rows);setOrgsError(null);return rows;
    }catch(e){
      setOrgs([]);
      setOrgsError(e instanceof ApiError?(e.body?.error||`Could not load organizations (HTTP ${e.status})`):"Could not load organizations");
      return [];
    }
  };
  // One call per org: active member addresses plus the tenant's app name.
  const rosterOf=async(id)=>{
    const j=await apiPost("/api/org",{action:"sa_org_detail",org_id:id},{email});
    const rows=j?.members||[];
    return {
      emails:rows.filter(m=>m.status==="active").map(m=>String(m.user_email||"").toLowerCase()).filter(Boolean),
      appName:j?.branding?.app_name||j?.org?.name||null,
      truncated:rows.length>=SA_DETAIL_MEMBER_CAP,
    };
  };

  useEffect(()=>{
    let dead=false;
    if(audience==="all"){setRoster(null);setRosterError(null);setBrandName(DEFAULT_BRAND);return undefined;}
    loadUsers(); // "solo" is the platform list minus every org member
    (async()=>{
      const list=await loadOrgs();
      if(dead)return;
      if(audience==="org"&&!orgId){setRoster(null);setRosterError(null);setBrandName(DEFAULT_BRAND);return;}
      setRosterBusy(true);setRosterError(null);
      try{
        if(audience==="org"){
          const r=await rosterOf(orgId);
          if(dead)return;
          setRoster({emails:r.emails,truncated:r.truncated});
          setBrandName(r.appName||DEFAULT_BRAND);
        }else{
          const all=[];let truncated=list.length>PUSH_ORG_MAX;
          for(const o of list.slice(0,PUSH_ORG_MAX)){
            const r=await rosterOf(o.id);
            if(dead)return;
            all.push(...r.emails);truncated=truncated||r.truncated;
          }
          if(dead)return;
          setRoster({emails:all,truncated});setBrandName(DEFAULT_BRAND);
        }
      }catch(e){
        if(dead)return;
        const code=e instanceof ApiError?e.body?.error:null;
        setRosterError(code==="super_admin_required"||code==="forbidden"
          ?"Reading an organization's roster needs the super-admin account (the address ADMIN_EMAIL on the API points at)."
          :"Could not read the organization roster — the audience is unresolved, so nothing will be sent.");
        setRoster(null);setBrandName(DEFAULT_BRAND);
      }
      setRosterBusy(false);
    })();
    return()=>{dead=true;};
  },[audience,orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  // The title follows the selected tenant's app name until the operator types their own.
  useEffect(()=>{if(!titleTouched)setTitle(brandName);},[brandName,titleTouched]);

  const selectedOrg=(orgs||[]).find(o=>o.id===orgId)||null;
  // null = broadcast to every subscription; an array = an explicit recipient list.
  const audienceEmails=audience==="all"?null
    :!roster?[]
    :audience==="org"?roster.emails
    :filterAudience(users||[],"solo",roster.emails).map(u=>String(u.email||"").toLowerCase());
  const audienceLabel=audience==="all"?"All users"
    :audience==="org"?`${selectedOrg?.name||"Organization"}${audienceEmails?` (${audienceEmails.length})`:""}`
    :`Solo users (${audienceEmails?audienceEmails.length:0})`;

  const filteredUsers=(search)=>(users||[]).filter(u=>
    !search||u.name?.toLowerCase().includes(search.toLowerCase())||u.email?.toLowerCase().includes(search.toLowerCase())
  );

  // ── Reusable multi-select user picker ──
  const UserPicker=({targetMode,setTargetMode,selected,setSelected,open,setOpen,search,setSearch,label})=>{
    const filtered=filteredUsers(search);
    const allSel=filtered.length>0&&filtered.every(u=>selected.has(u.email));
    const toggle=(e)=>{const ns=new Set(selected);ns.has(e)?ns.delete(e):ns.add(e);setSelected(ns);};
    const toggleAll=()=>{
      if(allSel){const ns=new Set(selected);filtered.forEach(u=>ns.delete(u.email));setSelected(ns);}
      else{const ns=new Set(selected);filtered.forEach(u=>ns.add(u.email));setSelected(ns);}
    };
    return(
      <div>
        <div style={{fontSize:10,fontWeight:700,color:V.text3,textTransform:"uppercase",letterSpacing:".06em",marginBottom:6}}>{label}</div>
        {/* Mode toggle */}
        <div style={{display:"flex",gap:6,marginBottom:8}}>
          {/* "All" means the audience chosen above, so it says so once an org or the solo
              set is selected — otherwise it would read as "everyone" and mean something else. */}
          {[{v:"all",l:audience==="all"?"📣 All Users":"📣 Whole audience"},{v:"selected",l:"👤 Specific Users"}].map(opt=>(
            <button key={opt.v} onClick={()=>{setTargetMode(opt.v);if(opt.v==="selected"&&users===null)loadUsers();}}
              style={{flex:1,padding:"8px 10px",borderRadius:8,border:`1px solid ${targetMode===opt.v?V.accent+"60":V.cardBorder}`,
                background:targetMode===opt.v?`${V.accent}12`:V.card,cursor:"pointer",
                fontSize:11,fontWeight:700,color:targetMode===opt.v?V.accent:V.text3,fontFamily:V.font,transition:"all .15s"}}>
              {opt.l}
            </button>
          ))}
        </div>

        {targetMode==="selected"&&(
          <div style={{position:"relative"}}>
            {/* Selected chips */}
            {selected.size>0&&(
              <div style={{display:"flex",flexWrap:"wrap",gap:4,marginBottom:6}}>
                {[...(users||[])].filter(u=>selected.has(u.email)).map(u=>(
                  <div key={u.email} style={{display:"flex",alignItems:"center",gap:4,padding:"3px 8px 3px 10px",
                    borderRadius:20,background:`${V.accent}15`,border:`1px solid ${V.accent}30`}}>
                    <span style={{fontSize:11,fontWeight:600,color:V.accent}}>{u.name||u.email.split("@")[0]}</span>
                    <button onClick={(e)=>{e.stopPropagation();toggle(u.email);}}
                      style={{background:"none",border:"none",cursor:"pointer",padding:0,lineHeight:1,
                        fontSize:12,color:V.accent,opacity:.7,marginTop:1}}>×</button>
                  </div>
                ))}
                <button onClick={()=>setSelected(new Set())}
                  style={{padding:"3px 8px",borderRadius:20,background:V.inputBg,
                    border:`1px solid ${V.cardBorder}`,cursor:"pointer",fontSize:10,color:V.text3,fontFamily:V.font}}>
                  Clear all
                </button>
              </div>
            )}

            {/* Dropdown trigger */}
            <button onClick={()=>{setOpen(v=>!v);if(!open&&users===null)loadUsers();}}
              style={{width:"100%",padding:"10px 12px",borderRadius:10,
                background:V.inputBg,border:`1px solid ${open?V.accent+"60":V.cardBorder}`,
                cursor:"pointer",display:"flex",justifyContent:"space-between",alignItems:"center",
                fontFamily:V.font,transition:"all .15s"}}>
              <span style={{fontSize:12,color:usersError?V.danger:selected.size>0?V.text:V.text3}}>
                {usersError?usersError:usersLoading?"Loading users…":selected.size>0?`${selected.size} user${selected.size!==1?"s":""} selected`:"Search and select users…"}
              </span>
              <span style={{fontSize:12,color:V.text3,transition:"transform .2s",
                display:"inline-block",transform:open?"rotate(180deg)":"rotate(0deg)"}}>▾</span>
            </button>

            {/* Dropdown list */}
            {open&&(
              <div style={{position:"absolute",left:0,right:0,top:"calc(100% + 4px)",zIndex:200,
                background:V.sheetBg,border:`1px solid ${V.accent}40`,borderRadius:12,
                boxShadow:"0 8px 32px rgba(0,0,0,0.4)",overflow:"hidden"}}>
                {/* Search inside dropdown */}
                <div style={{padding:"8px 10px",borderBottom:`1px solid ${V.cardBorder}`}}>
                  <input value={search} onChange={e=>setSearch(e.target.value)}
                    placeholder="Search by name or email…" autoFocus
                    style={{width:"100%",padding:"8px 12px",background:V.hoverBg,
                      border:`1px solid ${V.cardBorder}`,borderRadius:8,color:V.text,fontSize:12,
                      outline:"none",fontFamily:V.font,boxSizing:"border-box"}}/>
                </div>
                {/* Select all row */}
                <div onClick={toggleAll}
                  style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",cursor:"pointer",
                    borderBottom:`1px solid ${V.cardBorder}`,background:V.card}}>
                  <div style={{width:18,height:18,borderRadius:5,border:`2px solid ${allSel?V.accent:V.text3}`,flexShrink:0,
                    background:allSel?V.accent:"transparent",display:"flex",alignItems:"center",justifyContent:"center"}}>
                    {allSel&&<span style={{color:V.bg,fontSize:10,fontWeight:900,lineHeight:1}}>✓</span>}
                  </div>
                  <span style={{fontSize:12,fontWeight:700,color:allSel?V.accent:V.text3}}>
                    {allSel?"Deselect all":"Select all"} {search?`(${filtered.length} matching)`:`(${(users||[]).length} users)`}
                  </span>
                </div>
                {/* User rows */}
                <div style={{maxHeight:220,overflowY:"auto"}}>
                  {filtered.length===0&&<div style={{padding:16,textAlign:"center",fontSize:12,color:V.text3}}>No users found</div>}
                  {filtered.map(u=>{
                    const sel2=selected.has(u.email);
                    return(
                      <div key={u.email} onClick={()=>toggle(u.email)}
                        style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",cursor:"pointer",
                          background:sel2?`${V.accent}08`:"transparent",
                          borderBottom:`1px solid ${V.divider}`,transition:"background .1s"}}>
                        <div style={{width:18,height:18,borderRadius:5,border:`2px solid ${sel2?V.accent:V.text3}`,flexShrink:0,
                          background:sel2?V.accent:"transparent",display:"flex",alignItems:"center",justifyContent:"center"}}>
                          {sel2&&<span style={{color:V.bg,fontSize:10,fontWeight:900,lineHeight:1}}>✓</span>}
                        </div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:12,fontWeight:sel2?700:500,color:sel2?V.text:V.text3,
                            overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{u.name||u.email.split("@")[0]}</div>
                          <div style={{fontSize:10,color:V.text3,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{u.email}</div>
                        </div>
                        {u.lastSync&&<div style={{fontSize:8,color:V.text3,flexShrink:0}}>active</div>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {targetMode==="selected"&&selected.size>0&&(
          <div style={{fontSize:10,color:V.text3,marginTop:4}}>
            {selected.size} user{selected.size!==1?"s":""} selected
            {subCount!=null&&pushTargetMode==="all"?"":" · only users with push enabled will receive it"}
          </div>
        )}
      </div>
    );
  };

  // Every template names the brand the *recipients* actually have installed. Hard-coding
  // "IRONLOG" here sent a branded gym's members the name of an app they have never seen.
  const TEMPLATES=[
    {icon:"🔥",label:"Motivate",title:brandName,body:"Don't break the streak today — every rep counts. Let's go! 💪"},
    {icon:"⚡",label:"Double XP",title:"⚡ Double XP Today!",body:`Earn 2× XP on all workouts today only. Open ${brandName} and get after it!`},
    {icon:"🏆",label:"Challenge",title:"New Challenge Live",body:`A new weekly challenge just dropped. Open ${brandName} to compete now.`},
    {icon:"📣",label:"Update",title:`${brandName} Update`,body:"We just shipped new features. Open the app to check them out!"},
  ];

  const send=async()=>{
    if(!body.trim())return;
    setSending(true);setResult(null);
    const sendTitle=title.trim()||brandName;
    // An explicit user pick always wins; otherwise the audience decides. `null` means the
    // one true broadcast (every active subscription), an array means an address list.
    const targets=pushTargetMode==="selected"?[...pushSelected]
      :audience==="all"?null
      :audienceEmails;
    const label=pushTargetMode==="selected"?`${pushSelected.size} users`:audienceLabel;
    try{
      if(targets===null){
        // Single broadcast to all
        try{
          const json=await apiPost("/api/push",{action:"send",title:sendTitle,body:body.trim(),tag:"admin-broadcast"},{email});
          const entry={ts:new Date().toISOString(),title:sendTitle,body:body.trim(),
            target:label,sent:json.sent,failed:json.failed||0};
          const updated=[entry,...history].slice(0,20);setHistory(updated);LS.set("ft-admin-push-history",updated);
          setResult({ok:true,msg:`Sent to ${json.sent} device${json.sent!==1?"s":""} · ${json.failed||0} failed`});
          setBody("");
        }catch(e){
          if(e instanceof ApiError){
            setResult({ok:false,msg:e.status===401?"Unauthorized":e.status===403?"Forbidden":e.message});
          }else throw e;
        }
      }else{
        // Send to each resolved address individually — /api/push targets one at a time
        if(targets.length===0){
          setResult({ok:false,msg:pushTargetMode==="selected"?"Select at least one user"
            :rosterError?"The audience could not be resolved — see the note above"
            :audience==="org"&&!orgId?"Pick an organization first"
            :"No one matches this audience"});
          setSending(false);return;
        }
        const outcomes=await Promise.allSettled(targets.map(te=>
          apiPost("/api/push",{action:"send",title:sendTitle,body:body.trim(),tag:"admin-broadcast",email:te},{email})
            .then(j=>({ok:true,sent:j.sent||0,failed:j.failed||0}))
            .catch(()=>({ok:false,sent:0,failed:0}))
        ));
        const totalSent=outcomes.reduce((a,r)=>a+(r.value?.sent||0),0);
        const totalFailed=outcomes.reduce((a,r)=>a+(r.value?.failed||0),0);
        const entry={ts:new Date().toISOString(),title:sendTitle,body:body.trim(),
          target:label,sent:totalSent,failed:totalFailed};
        const updated=[entry,...history].slice(0,20);setHistory(updated);LS.set("ft-admin-push-history",updated);
        setResult({ok:totalSent>0,msg:`Sent to ${totalSent} device${totalSent!==1?"s":""} · ${totalFailed} failed · ${targets.length} user${targets.length!==1?"s":""} targeted`});
        if(totalSent>0)setBody("");
      }
    }catch(e){setResult({ok:false,msg:e.message});}
    setSending(false);
  };

  const sendInApp=async()=>{
    // The in-app queue is per-user anyway, so "all" here means the current audience.
    const targets=inAppTargetMode==="all"
      ?(audience==="all"?(users||[]).map(u=>u.email):(audienceEmails||[]))
      :[...inAppSelected];
    if(targets.length===0){setNotifResult({ok:false,msg:inAppTargetMode==="selected"?"Select at least one user":"No users loaded — load users first"});return;}
    const activeFlags=Object.fromEntries(Object.entries(notifFlags).filter(([,v])=>v));
    if(notifFlags.custom&&customMsg.trim()){activeFlags.custom=customMsg.trim();if(customTitle.trim())activeFlags.custom_title=customTitle.trim();}
    if(!Object.keys(activeFlags).length){setNotifResult({ok:false,msg:"Select at least one notification type"});return;}
    setNotifSending(true);setNotifResult(null);
    const results=await Promise.allSettled(targets.map(te=>
      apiPost("/api/admin",{action:"set_notif_flags",email:te,flags:activeFlags},{email}).then(()=>true)
    ));
    const ok=results.filter(r=>r.value===true).length;
    setNotifResult({ok:ok>0,msg:`Queued for ${ok}/${targets.length} user${targets.length!==1?"s":""} — fires when they open the app`});
    setNotifSending(false);
  };

  const fmtTs=(ts)=>{const d=new Date(ts);return d.toLocaleDateString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"});};

  return(
    <div style={{display:"flex",flexDirection:"column",gap:14}}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
        <div>
          <div style={{fontSize:16,fontWeight:800,color:V.text}}>📣 Push Broadcast</div>
          <div style={{fontSize:11,color:V.text3}}>Send push notifications to all users or specific people</div>
        </div>
        {subCount!=null&&(
          <div style={{textAlign:"right",padding:"6px 12px",borderRadius:10,background:`${V.accent}10`,border:`1px solid ${V.accent}20`}}>
            <div style={{fontSize:18,fontWeight:900,color:V.accent,fontFamily:V.mono}}>{subCount}</div>
            <div style={{fontSize:9,color:V.text3}}>SUBSCRIBED</div>
          </div>
        )}
      </div>

      {/* Quick templates */}
      <div>
        <div style={{fontSize:10,fontWeight:700,color:V.text3,textTransform:"uppercase",letterSpacing:".06em",marginBottom:6}}>Quick Templates</div>
        <div style={{display:"grid",gridTemplateColumns:isDesktop?"1fr 1fr 1fr":"1fr 1fr",gap:6}}>
          {TEMPLATES.map(t=>(
            <button key={t.label} onClick={()=>{setTitle(t.title);setTitleTouched(true);setBody(t.body);}}
              style={{padding:"8px 10px",borderRadius:8,border:`1px solid ${body===t.body?V.accent+"40":V.cardBorder}`,
                background:body===t.body?`${V.accent}12`:V.card,cursor:"pointer",textAlign:"left",transition:"all .15s"}}>
              <div style={{fontSize:12}}>{t.icon} <span style={{fontSize:11,fontWeight:700,color:V.text}}>{t.label}</span></div>
              <div style={{fontSize:9,color:V.text3,marginTop:2,lineHeight:1.3}}>{t.body.slice(0,40)}…</div>
            </button>
          ))}
        </div>
      </div>

      {/* Compose card */}
      <Card style={{padding:14,display:"flex",flexDirection:"column",gap:12}}>
        {/* Title */}
        <div>
          <div style={{fontSize:10,fontWeight:700,color:V.text3,textTransform:"uppercase",letterSpacing:".06em",marginBottom:4}}>Title</div>
          <input value={title} onChange={e=>{setTitle(e.target.value);setTitleTouched(true);}} maxLength={64} aria-label="Notification title" data-testid="push-title"
            style={{width:"100%",padding:"10px 12px",background:V.inputBg,border:`1px solid ${V.cardBorder}`,
              borderRadius:8,color:V.text,fontSize:13,outline:"none",fontFamily:V.font,boxSizing:"border-box"}}/>
        </div>
        {/* Message */}
        <div>
          <div style={{fontSize:10,fontWeight:700,color:V.text3,textTransform:"uppercase",letterSpacing:".06em",marginBottom:4}}>Message</div>
          <textarea value={body} onChange={e=>setBody(e.target.value)} maxLength={200} rows={3}
            placeholder="Type your message..."
            style={{width:"100%",padding:"10px 12px",background:V.inputBg,border:`1px solid ${V.cardBorder}`,
              borderRadius:8,color:V.text,fontSize:13,outline:"none",fontFamily:V.font,resize:"none",boxSizing:"border-box"}}/>
          <div style={{fontSize:9,color:body.length>180?V.warn:V.text3,textAlign:"right",marginTop:2}}>{body.length}/200</div>
        </div>

        {/* Audience — who this goes to before any individual picking */}
        <div data-testid="push-audience">
          <div style={{fontSize:10,fontWeight:700,color:V.text3,textTransform:"uppercase",letterSpacing:".06em",marginBottom:6}}>Audience</div>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {[{v:"all",l:"🌍 Everyone"},{v:"org",l:"🏢 One organization"},{v:"solo",l:"👤 Solo users only"}].map(opt=>(
              <button key={opt.v} onClick={()=>{setAudience(opt.v);if(opt.v!=="org")setOrgId("");}} data-testid={`push-audience-${opt.v}`}
                aria-pressed={audience===opt.v}
                style={{flex:"1 1 120px",minHeight:44,paddingTop:8,paddingBottom:8,paddingLeft:10,paddingRight:10,borderRadius:8,
                  border:`1px solid ${audience===opt.v?V.accent+"60":V.cardBorder}`,
                  background:audience===opt.v?`${V.accent}12`:V.card,cursor:"pointer",
                  fontSize:11,fontWeight:700,color:audience===opt.v?V.accent:V.text3,fontFamily:V.font}}>
                {opt.l}
              </button>
            ))}
          </div>
          {/* Switching tenants re-derives the title only while the operator has not typed
              their own — their wording is never silently replaced. */}
          {audience==="org"&&(
            <select value={orgId} onChange={e=>setOrgId(e.target.value)} aria-label="Organization" data-testid="push-org-select"
              style={{width:"100%",minHeight:44,marginTop:8,paddingTop:10,paddingBottom:10,paddingLeft:12,paddingRight:12,background:V.inputBg,
                border:`1px solid ${V.cardBorder}`,borderRadius:8,color:V.text,fontSize:13,outline:"none",fontFamily:V.font,boxSizing:"border-box"}}>
              <option value="">{orgs===null?"Loading organizations…":orgs.length?"Select an organization…":"No organizations"}</option>
              {(orgs||[]).map(o=>(<option key={o.id} value={o.id}>{o.name} ({o.slug}){o.status&&o.status!=="active"?` · ${o.status}`:""}</option>))}
            </select>
          )}
          {orgsError&&<div style={{fontSize:10,color:V.danger,marginTop:6,lineHeight:1.5}}>{orgsError}</div>}
          {rosterError&&<div data-testid="push-roster-error" style={{fontSize:10,color:V.danger,marginTop:6,lineHeight:1.5}}>{rosterError}</div>}
          {audience!=="all"&&!rosterError&&(
            <div data-testid="push-audience-count" style={{fontSize:10,color:V.text3,marginTop:6,lineHeight:1.5}}>
              {rosterBusy?"Resolving the audience…"
                :audience==="org"&&!orgId?"Pick an organization to see how many members it has."
                :`${audienceLabel} · sent one address at a time (/api/push cannot target an organization).`}
              {roster?.truncated?" The roster hit its row cap, so some members are missing.":""}
              {audience==="org"&&orgId&&!titleTouched?` Title defaults to “${brandName}”, the name these members see on their home screen.`:""}
            </div>
          )}
        </div>

        {/* Target picker — call as a function (not <UserPicker/>) so it stays mounted
            across renders; rendering it as an element gave it a new type each render
            and remounted the search input on every keystroke (focus loss). */}
        {UserPicker({
          targetMode:pushTargetMode, setTargetMode:setPushTargetMode,
          selected:pushSelected, setSelected:setPushSelected,
          open:dropdownOpen, setOpen:setDropdownOpen,
          search:userSearch, setSearch:setUserSearch,
          label:"Send To",
        })}

        {result&&(
          <div style={{padding:"10px 12px",borderRadius:8,
            background:result.ok?`${V.accent}10`:`${V.danger}10`,
            border:`1px solid ${result.ok?V.accent+"30":V.danger+"30"}`,
            fontSize:12,color:result.ok?V.accent:V.danger,fontWeight:600}}>
            {result.ok?"✅ ":"❌ "}{result.msg}
          </div>
        )}

        <Btn full onClick={send} disabled={!body.trim()||sending}
          s={{background:sending?"rgba(255,255,255,0.06)":undefined}}>
          {sending?"Sending…":`📣 ${pushTargetMode==="all"
            ?(audience==="all"?`Send to All${subCount!=null?" ("+subCount+")":""}`:`Send to ${audienceLabel}`)
            :pushSelected.size>0?`Send to ${pushSelected.size} User${pushSelected.size!==1?"s":""}`:"Send Push"}`}
        </Btn>

        <div style={{fontSize:10,color:V.text3,textAlign:"center",lineHeight:1.5}}>
          Only users who granted notification permission will receive this.<br/>
          iOS requires the app to be installed to home screen.
        </div>
      </Card>

      {/* ── In-App Notification section ── */}
      <div style={{borderTop:`1px solid ${V.cardBorder}`,paddingTop:14}}>
        <div style={{fontSize:14,fontWeight:800,color:V.text,marginBottom:4}}>🔔 In-App Notifications</div>
        <div style={{fontSize:11,color:V.text3,marginBottom:12}}>Fires as a banner the next time the user opens the app. No push permission needed.</div>

        {/* In-app target */}
        <div style={{marginBottom:12}}>
          {UserPicker({
            targetMode:inAppTargetMode, setTargetMode:setInAppTargetMode,
            selected:inAppSelected, setSelected:setInAppSelected,
            open:inAppDropdownOpen, setOpen:setInAppDropdownOpen,
            search:inAppSearch, setSearch:setInAppSearch,
            label:"Target Users",
          })}
          {inAppTargetMode==="all"&&users===null&&(
            <button onClick={loadUsers}
              style={{marginTop:6,padding:"6px 14px",borderRadius:8,background:V.inputBg,
                border:`1px solid ${V.cardBorder}`,cursor:"pointer",fontSize:11,color:V.text3,fontFamily:V.font}}>
              {usersLoading?"Loading…":"Load users for broadcast count"}
            </button>
          )}
          {inAppTargetMode==="all"&&users!==null&&(
            <div style={{fontSize:10,color:V.text3,marginTop:4}}>Will queue for all {users.length} users</div>
          )}
        </div>

        {/* Notification type toggles */}
        <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:10}}>
          {NOTIF_TYPES.map(nt=>(
            <div key={nt.key} onClick={()=>setNotifFlags(f=>({...f,[nt.key]:!f[nt.key]}))}
              style={{display:"flex",alignItems:"center",gap:12,padding:"10px 12px",borderRadius:10,cursor:"pointer",
                border:`1px solid ${notifFlags[nt.key]?V.accent+"40":V.cardBorder}`,
                background:notifFlags[nt.key]?`${V.accent}08`:V.card,transition:"all .15s"}}>
              <div style={{width:36,height:22,borderRadius:11,border:`2px solid ${notifFlags[nt.key]?V.accent:V.text3}`,
                background:notifFlags[nt.key]?V.accent:"transparent",position:"relative",flexShrink:0,transition:"all .2s"}}>
                <div style={{position:"absolute",top:1,left:notifFlags[nt.key]?14:1,width:16,height:16,borderRadius:"50%",
                  background:notifFlags[nt.key]?"#fff":"rgba(255,255,255,0.4)",transition:"left .2s"}}/>
              </div>
              <span style={{fontSize:16,flexShrink:0}}>{nt.icon}</span>
              <div style={{flex:1}}>
                <div style={{fontSize:12,fontWeight:700,color:notifFlags[nt.key]?V.text:V.text3}}>{nt.label}</div>
                <div style={{fontSize:10,color:V.text3}}>{nt.desc}</div>
              </div>
            </div>
          ))}
        </div>

        {notifFlags.custom&&(
          <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:10}}>
            <input value={customTitle} onChange={e=>setCustomTitle(e.target.value)} placeholder={`Title (optional, defaults to ${brandName})`}
              style={{width:"100%",padding:"10px 12px",background:V.inputBg,border:`1px solid ${V.cardBorder}`,
                borderRadius:8,color:V.text,fontSize:13,outline:"none",fontFamily:V.font,boxSizing:"border-box"}}/>
            <textarea value={customMsg} onChange={e=>setCustomMsg(e.target.value)} rows={2} maxLength={200}
              placeholder="Custom message text..."
              style={{width:"100%",padding:"10px 12px",background:V.inputBg,border:`1px solid ${V.cardBorder}`,
                borderRadius:8,color:V.text,fontSize:13,outline:"none",fontFamily:V.font,resize:"none",boxSizing:"border-box"}}/>
          </div>
        )}

        {notifResult&&(
          <div style={{padding:"10px 12px",borderRadius:8,marginBottom:8,
            background:notifResult.ok?`${V.accent}10`:`${V.danger}10`,
            border:`1px solid ${notifResult.ok?V.accent+"30":V.danger+"30"}`,
            fontSize:12,color:notifResult.ok?V.accent:V.danger,fontWeight:600}}>
            {notifResult.ok?"✅ ":"❌ "}{notifResult.msg}
          </div>
        )}
        <button onClick={sendInApp} disabled={notifSending||!Object.values(notifFlags).some(Boolean)}
          style={{width:"100%",padding:"12px",borderRadius:10,border:"none",cursor:"pointer",fontFamily:V.font,
            fontSize:13,fontWeight:800,transition:"all .2s",
            background:Object.values(notifFlags).some(Boolean)?`linear-gradient(135deg,${V.accent},${V.accent2})`:V.hoverBg,
            color:Object.values(notifFlags).some(Boolean)?V.bg:V.text3}}>
          {notifSending?"Queuing…":"🔔 Queue In-App Notification"}
        </button>
      </div>

      {/* Send history */}
      {history.length>0&&(
        <div>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
            <div style={{fontSize:11,fontWeight:700,color:V.text3,textTransform:"uppercase",letterSpacing:".06em"}}>Send History</div>
            <button onClick={()=>{setHistory([]);LS.set("ft-admin-push-history",[]);}}
              style={{background:"none",border:"none",cursor:"pointer",fontSize:10,color:V.text3}}>Clear</button>
          </div>
          {history.map((h,i)=>(
            <Card key={i} style={{padding:"10px 12px",marginBottom:6}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:8}}>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:12,fontWeight:700,color:V.text,marginBottom:2}}>{h.title}</div>
                  <div style={{fontSize:11,color:V.text3,lineHeight:1.4}}>{h.body}</div>
                  <div style={{fontSize:10,color:V.text3,marginTop:4}}>→ {h.target}</div>
                </div>
                <div style={{textAlign:"right",flexShrink:0}}>
                  <div style={{fontSize:13,fontWeight:800,color:V.accent,fontFamily:V.mono}}>{h.sent}</div>
                  <div style={{fontSize:9,color:V.text3}}>sent</div>
                  {h.failed>0&&<div style={{fontSize:9,color:V.danger}}>{h.failed} failed</div>}
                  <div style={{fontSize:9,color:V.text3,marginTop:2}}>{fmtTs(h.ts)}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}


// ─── Admin Hub (admin nav tab — only renders for admins) ───

// ═══════════════════════════════════════════════════
// ADMIN USER AUDIT — see exactly what any user sees
// ═══════════════════════════════════════════════════
